/**
 * Shareable deep links + the Web Share wrapper behind every share button.
 *
 * This is a web app, so a share link is just a short path on the current
 * origin — `/e/<id>` for an event, `/v/<id>` for a venue. On Vercel these paths
 * are rewritten to a server function that injects Open Graph tags (so the link
 * unfurls with a preview card on iMessage / WhatsApp / X / Slack / Instagram
 * DMs), then hands the normal SPA to real visitors, who land on that
 * event/venue (see App boot's deep-link parse).
 */
export function webBaseUrl(): string {
  return window.location.origin;
}

export function eventShareUrl(eventId: string): string {
  return `${webBaseUrl()}/e/${eventId}`;
}

export function venueShareUrl(venueId: string): string {
  return `${webBaseUrl()}/v/${venueId}`;
}

/** A shared link that landed on the app, parsed from the boot URL path. */
export interface ShareDeepLink {
  screen: 'events' | 'map';
  /** Set for `/e/<id>` — opens that event's detail sheet on the Events tab. */
  eventId?: string;
  /** Set for `/v/<id>` — flies to and selects that venue on the Map tab. */
  focusVenueId?: string;
}

/**
 * Parse the boot URL path into a deep-link target, or null for a normal entry.
 * Matches the share-link shapes (`/e/<id>`, `/v/<id>`) the app emits and that
 * the Vercel rewrite serves. Native always boots at `/`, so this is a no-op
 * there — deep links into the native shell would need universal links.
 */
export function parseShareDeepLink(pathname: string): ShareDeepLink | null {
  const ev = pathname.match(/^\/e\/([^/?#]+)/);
  if (ev?.[1]) return { screen: 'events', eventId: decodeURIComponent(ev[1]) };
  const ve = pathname.match(/^\/v\/([^/?#]+)/);
  if (ve?.[1]) return { screen: 'map', focusVenueId: decodeURIComponent(ve[1]) };
  return null;
}

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'unsupported';

/**
 * Share a link via the native share sheet, falling back to clipboard copy when
 * Web Share is unavailable (most desktop browsers). Returns what happened so
 * the caller can fire the right analytics and toast. Never throws — a user
 * dismissing the sheet resolves to `cancelled`, not an error.
 */
export async function shareLink(opts: {
  url: string;
  title?: string;
  text?: string;
}): Promise<ShareResult> {
  const { url, title, text } = opts;
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;

  if (nav?.share) {
    try {
      const data: ShareData = { url };
      if (title) data.title = title;
      if (text) data.text = text;
      await nav.share(data);
      return 'shared';
    } catch (err) {
      // AbortError = user closed the sheet; treat as a no-op, not a failure.
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
      // Fall through to clipboard on any other share failure.
    }
  }

  if (nav?.clipboard?.writeText) {
    try {
      await nav.clipboard.writeText(url);
      return 'copied';
    } catch {
      return 'unsupported';
    }
  }

  return 'unsupported';
}
